import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, Refresh } from '@mui/icons-material';
import { useRegenerateAPIKey } from '../hooks/useApi';

interface ApiKeyCardProps {
  apiKey: string;
  email: string;
  userId: string;
  enabled: boolean;
}

export function ApiKeyCard({ apiKey, email, userId, enabled }: ApiKeyCardProps) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const regenerate = useRegenerateAPIKey();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRegenerateConfirm = async () => {
    try {
      await regenerate.mutateAsync();
      setRegenerateDialogOpen(false);
    } catch {
      // Error shown via regenerate.isError in dialog
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your API Key
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Email: {email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              User ID: {userId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {enabled ? 'Active' : 'Disabled'}
            </Typography>
          </Box>

          {!enabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your API key is currently disabled. Please contact support.
            </Alert>
          )}

          <TextField
            fullWidth
            value={apiKey}
            type={showKey ? 'text' : 'password'}
            label="API Key"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowKey(!showKey)} edge="end">
                    {showKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={handleCopy}
              disabled={!enabled}
            >
              {copied ? 'Copied!' : 'Copy API Key'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Refresh />}
              onClick={() => setRegenerateDialogOpen(true)}
              disabled={!enabled}
            >
              Regenerate API key
            </Button>
          </Box>

          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            Keep your API key secure and never share it publicly.
          </Typography>
        </CardContent>
      </Card>

      <Dialog
        open={regenerateDialogOpen}
        onClose={() => !regenerate.isPending && setRegenerateDialogOpen(false)}
      >
        <DialogTitle>Regenerate API key?</DialogTitle>
        <DialogContent>
          <Typography>
            This will invalidate your current API key immediately. You will need to update any
            applications using the old key with the new one. Continue?
          </Typography>
          {regenerate.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(regenerate.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRegenerateDialogOpen(false)}
            disabled={regenerate.isPending}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleRegenerateConfirm}
            disabled={regenerate.isPending}
            startIcon={regenerate.isPending ? <CircularProgress size={16} /> : <Refresh />}
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
